var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, Stack, Typography, styled, } from '@mui/material';
import { flatMap, sortBy, times } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { OrgShrinkageFactorType, ShrinkageCategory } from '../../../enum';
import { Autocomplete, InputLabel, MultiAutocomplete, NumberInput, RadioGroup, UnControlledFormField, } from '../../common';
import { useFormlessContext } from '../../hooks';
import { PeriodSelector } from '../common/quickInputDialog';
import { useDetailTimeline, useLocationOptions, usePeriods, usePlanType, useQueueLocations } from '../selectors';
import { useWizardSimpleContext } from '../useWizardSimpleContext';
import { useShrinkageTable } from './selectors';
import { ShrinkageUnitMode, getBusinessDays, getShrinkagePercentValue, getShrinkageUnitInputMeta, resolveShrinkageStaffSettings, } from './useShrinkageUnit';
var StyledActionButton = styled(Button)(function () { return ({
    textTransform: 'uppercase',
}); });
var StyledDialogActions = styled(DialogActions)(function () { return ({
    alignItems: 'center',
    justifyContent: 'center',
}); });
var NO_SUBCATEGORY = 'none';
var CATEGORY_ORDER = [ShrinkageCategory.OutOfOffice, ShrinkageCategory.InOffice];
export var stripDisplaySuffix = function (name) { return (name !== null && name !== void 0 ? name : '').replace(/\s\+$/, '').trim(); };
var normalizeSubcategory = function (subcategory) { return (subcategory === null || subcategory === void 0 ? void 0 : subcategory.trim()) || null; };
export var ShrinkageQuickInputDialog = function (_a) {
    var _b;
    var open = _a.open, onClose = _a.onClose, queueLocationId = _a.queueLocationId, unitModeProp = _a.unitMode, setUnitModeProp = _a.setUnitMode, isUnitModeEnabledProp = _a.isUnitModeEnabled;
    var t = useTranslation(['translation', 'wizard']).t;
    var plan = ((_b = useWizardSimpleContext()) !== null && _b !== void 0 ? _b : {}).plan;
    var planType = usePlanType();
    var _c = (plan !== null && plan !== void 0 ? plan : {}).periodLength, periodLength = _c === void 0 ? 0 : _c;
    var timelines = useDetailTimeline();
    var shrinkageData = useShrinkageTable(queueLocationId);
    var queueLocations = useQueueLocations();
    var methods = useFormlessContext();
    var clearErrors = useFormContext().clearErrors;
    var setValue = (methods !== null && methods !== void 0 ? methods : {}).setValue;
    var watchShrinkageData = useWatch({ name: 'shrinkages', control: methods === null || methods === void 0 ? void 0 : methods.control });
    var periods = usePeriods();
    var _d = useState(ShrinkageCategory.OutOfOffice), category = _d[0], setCategory = _d[1];
    var _e = useState(''), factor = _e[0], setFactor = _e[1];
    var _f = useState(NO_SUBCATEGORY), subcategory = _f[0], setSubcategory = _f[1];
    var _g = useState(null), valueLocal = _g[0], setValueLocal = _g[1];
    var _h = useState([]), selectedLocations = _h[0], setSelectedLocations = _h[1];
    var _j = useState([]), selectedWeeks = _j[0], setSelectedWeeks = _j[1];
    var _k = useState(OrgShrinkageFactorType.Planned), shrinkageType = _k[0], setShrinkageType = _k[1];
    var _l = useState(ShrinkageUnitMode.Percent), localUnitMode = _l[0], setLocalUnitMode = _l[1];
    var locationOptions = useLocationOptions();
    var showDateColumn = false;
    var unitMode = unitModeProp !== null && unitModeProp !== void 0 ? unitModeProp : localUnitMode;
    var setUnitMode = setUnitModeProp !== null && setUnitModeProp !== void 0 ? setUnitModeProp : setLocalUnitMode;
    var staffSettings = useMemo(function () { return resolveShrinkageStaffSettings(queueLocations, queueLocationId); }, [queueLocationId, queueLocations]);
    var isUnitModeEnabled = isUnitModeEnabledProp !== null && isUnitModeEnabledProp !== void 0 ? isUnitModeEnabledProp : (staffSettings.paidHoursPerDay != null && staffSettings.workingDaysPerWeek != null);
    var unitInputMeta = useMemo(function () { return getShrinkageUnitInputMeta(unitMode); }, [unitMode]);
    var categoryOptions = useMemo(function () { return [
        { id: ShrinkageCategory.OutOfOffice, name: t('wizard:shrinkage.category.out_of_office') },
        { id: ShrinkageCategory.InOffice, name: t('wizard:shrinkage.category.in_office') },
    ]; }, [t]);
    var unitOptions = useMemo(function () { return [
        { id: ShrinkageUnitMode.Percent, name: t('wizard:shrinkage.unit.percent') },
        { id: ShrinkageUnitMode.Days, name: t('wizard:shrinkage.unit.days') },
        { id: ShrinkageUnitMode.Hours, name: t('wizard:shrinkage.unit.hours') },
    ]; }, [t]);
    var selectedShrinkages = useMemo(function () {
        if (queueLocationId === null)
            return flatMap(shrinkageData);
        var selectedLocationSet = new Set(selectedLocations);
        return flatMap(queueLocations.filter(function (location) { return selectedLocationSet.has(location.id); }), 'shrinkages');
    }, [queueLocationId, queueLocations, selectedLocations, shrinkageData]);
    var factorOptions = useMemo(function () {
        var uniqueFactorsMap = new Map();
        var index = 0;
        selectedShrinkages.forEach(function (shrinkage) {
            var _a;
            var shrinkageCategory = shrinkage === null || shrinkage === void 0 ? void 0 : shrinkage.shrinkageCategory;
            if (!shrinkage || !shrinkageCategory)
                return;
            var sourceName = stripDisplaySuffix(shrinkage.name);
            var shrinkageFactorType = (_a = shrinkage.shrinkageType) !== null && _a !== void 0 ? _a : null;
            var factorKey = "".concat(shrinkageCategory, "|").concat(sourceName, "|").concat(shrinkageFactorType);
            var subcategoryName = normalizeSubcategory(shrinkage.shrinkageSubcategory);
            if (!uniqueFactorsMap.has(factorKey)) {
                uniqueFactorsMap.set(factorKey, {
                    name: sourceName,
                    sourceName: sourceName,
                    category: shrinkageCategory,
                    shrinkageType: shrinkageFactorType,
                    subcategoriesSet: new Set(),
                    index: index++,
                });
            }
            if (subcategoryName) {
                uniqueFactorsMap.get(factorKey).subcategoriesSet.add(subcategoryName);
            }
        });
        var options = Array.from(uniqueFactorsMap.entries()).map(function (_a) {
            var key = _a[0], factorVal = _a[1];
            return ({
                id: key,
                name: factorVal.name,
                sourceName: factorVal.sourceName,
                category: factorVal.category,
                shrinkageType: factorVal.shrinkageType,
                subcategories: Array.from(factorVal.subcategoriesSet),
                index: factorVal.index,
            });
        });
        return sortBy(options, [
            function (option) {
                var categoryIndex = CATEGORY_ORDER.indexOf(option.category);
                return categoryIndex === -1 ? CATEGORY_ORDER.length : categoryIndex;
            },
            'index',
        ]);
    }, [selectedShrinkages]);
    var filteredFactorOptions = useMemo(function () { return factorOptions.filter(function (option) { return option.category === category && option.shrinkageType === shrinkageType; }); }, [category, factorOptions, shrinkageType]);
    var selectedFactor = useMemo(function () { var _a; return (_a = factorOptions.find(function (option) { return option.id === factor; })) !== null && _a !== void 0 ? _a : null; }, [factor, factorOptions]);
    var subcategoryOptions = useMemo(function () { var _a, _b; return (_b = (_a = selectedFactor === null || selectedFactor === void 0 ? void 0 : selectedFactor.subcategories) === null || _a === void 0 ? void 0 : _a.map(function (sub) { return ({ id: sub, name: sub }); })) !== null && _b !== void 0 ? _b : []; }, [selectedFactor]);
    var hasSubcategories = subcategoryOptions.length > 0;
    var periodLabels = useMemo(function () {
        var _a, _b;
        if (!(timelines === null || timelines === void 0 ? void 0 : timelines.length))
            return [];
        var names = (_b = (_a = timelines[0]) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : [];
        return times(periodLength, function (i) { return ({ label: names[i], date: '' }); });
    }, [timelines, periodLength]);
    var resetFactorStates = function () {
        setFactor('');
        setSubcategory(NO_SUBCATEGORY);
    };
    var selectFirstFactor = function (nextCategory, nextShrinkageType) {
        var _a, _b, _c, _d;
        var matchingFactors = factorOptions.filter(function (option) { return option.category === nextCategory && option.shrinkageType === nextShrinkageType; });
        var selectFactor = (_a = matchingFactors.find(function (option) { return option.id === factor; })) !== null && _a !== void 0 ? _a : matchingFactors[0];
        setFactor((_b = selectFactor === null || selectFactor === void 0 ? void 0 : selectFactor.id) !== null && _b !== void 0 ? _b : '');
        setSubcategory((_d = (_c = selectFactor === null || selectFactor === void 0 ? void 0 : selectFactor.subcategories) === null || _c === void 0 ? void 0 : _c[0]) !== null && _d !== void 0 ? _d : NO_SUBCATEGORY);
    };
    useEffect(function () {
        if (open) {
            resetFactorStates();
            selectFirstFactor(category, shrinkageType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category, selectedLocations, shrinkageType]);
    var resetState = useCallback(function () {
        setCategory(ShrinkageCategory.OutOfOffice);
        setValueLocal(null);
        setSelectedLocations(queueLocationId ? [queueLocationId] : []);
        setSelectedWeeks([]);
        setShrinkageType(OrgShrinkageFactorType.Planned);
    }, [queueLocationId]);
    useEffect(function () {
        if (open) {
            resetState();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    var handleClose = function () {
        onClose();
        resetState();
        resetFactorStates();
    };
    var handleCategoryChange = function (val) {
        var _a;
        var nextCategory = (_a = val) !== null && _a !== void 0 ? _a : ShrinkageCategory.OutOfOffice;
        setCategory(nextCategory);
        selectFirstFactor(nextCategory, shrinkageType);
    };
    var handleSelectAll = function (checked) {
        setSelectedWeeks(checked ? times(periodLength) : []);
    };
    var handleUnitModeChange = function (nextMode) {
        var _a;
        var nextUnitMode = (_a = nextMode) !== null && _a !== void 0 ? _a : ShrinkageUnitMode.Percent;
        setUnitMode(nextUnitMode);
        setValueLocal(null);
    };
    var handleWeekToggle = function (idx) {
        setSelectedWeeks(function (prev) { return (prev.includes(idx) ? prev.filter(function (w) { return w !== idx; }) : __spreadArray(__spreadArray([], prev, true), [idx], false)); });
    };
    var isMatchingFactor = useCallback(function (shrinkage, selectedLocationIds) {
        var _a;
        if (!selectedFactor)
            return false;
        var sameLocation = (queueLocations === null || queueLocations === void 0 ? void 0 : queueLocations.length)
            ? selectedLocationIds.includes(Number(shrinkage.queueLocationId))
            : !shrinkage.queueLocationId;
        return (sameLocation &&
            stripDisplaySuffix(shrinkage.name) === selectedFactor.sourceName &&
            shrinkage.shrinkageCategory === selectedFactor.category &&
            normalizeSubcategory(shrinkage.shrinkageSubcategory) ===
                (subcategory === NO_SUBCATEGORY ? null : subcategory) &&
            ((_a = shrinkage.shrinkageType) !== null && _a !== void 0 ? _a : null) === selectedFactor.shrinkageType);
    }, [queueLocations === null || queueLocations === void 0 ? void 0 : queueLocations.length, selectedFactor, subcategory]);
    var applyQuickInput = useCallback(function () {
        var _a;
        if (valueLocal === null || !selectedFactor || !setValue) {
            return false;
        }
        for (var _i = 0, selectedWeeks_1 = selectedWeeks; _i < selectedWeeks_1.length; _i++) {
            var periodIdx = selectedWeeks_1[_i];
            var periodShrinkages = (_a = watchShrinkageData === null || watchShrinkageData === void 0 ? void 0 : watchShrinkageData[periodIdx]) !== null && _a !== void 0 ? _a : [];
            for (var shrinkageIdx = 0; shrinkageIdx < periodShrinkages.length; shrinkageIdx++) {
                var shrinkage = periodShrinkages[shrinkageIdx];
                if (!isMatchingFactor(shrinkage, selectedLocations)) {
                    continue;
                }
                var settings = queueLocations.length
                    ? resolveShrinkageStaffSettings(queueLocations, shrinkage.queueLocationId)
                    : staffSettings;
                var businessDays = getBusinessDays(planType, settings.workingDaysPerWeek, periods, periodIdx);
                var percentValue = getShrinkagePercentValue(valueLocal, unitMode, settings.paidHoursPerDay, businessDays);
                if (percentValue === null) {
                    return false;
                }
                clearErrors('shrinkages');
                setValue("shrinkages.".concat(periodIdx, ".").concat(shrinkageIdx, ".percent"), percentValue, {
                    shouldDirty: true,
                });
            }
        }
        return true;
    }, [
        clearErrors,
        isMatchingFactor,
        periods,
        planType,
        queueLocations,
        selectedFactor,
        selectedLocations,
        selectedWeeks,
        setValue,
        staffSettings,
        unitMode,
        valueLocal,
        watchShrinkageData,
    ]);
    var handleDone = function () {
        if (applyQuickInput())
            handleClose();
    };
    var handleSaveAndAddAnother = function () {
        if (!applyQuickInput())
            return;
        resetState();
    };
    var hiddenLabel = _jsx(InputLabel, __assign({ sx: { visibility: 'hidden' } }, { children: '\u00A0' }));
    var canSubmit = valueLocal !== null &&
        selectedWeeks.length > 0 &&
        !!selectedFactor &&
        (!(queueLocations === null || queueLocations === void 0 ? void 0 : queueLocations.length) || selectedLocations.length > 0);
    return (_jsxs(Dialog, __assign({ open: open, onClose: handleClose, maxWidth: "md", fullWidth: true }, { children: [_jsx(DialogTitle, __assign({ component: "div" }, { children: _jsx(Typography, __assign({ variant: "h3" }, { children: t('quickInput.title') })) })), _jsx(DialogContent, { children: _jsxs(Stack, __assign({ spacing: 4 }, { children: [_jsxs(Stack, __assign({ direction: "row", spacing: 4 }, { children: [_jsx(UnControlledFormField, __assign({ label: t('wizard:shrinkage.type'), sx: { flex: 1 } }, { children: _jsx(Autocomplete, { options: categoryOptions, value: category, getOptionValue: function (o) { return o.id; }, onChange: handleCategoryChange, size: "small", placeholder: t('translation:autocomplete.pleaseSelect') }) })), _jsx(UnControlledFormField, __assign({ label: t('wizard:shrinkage.shrinkageFactor'), sx: { flex: 1, minWidth: '12rem' } }, { children: _jsx(Autocomplete, { options: filteredFactorOptions, value: factor, placeholder: t('translation:autocomplete.pleaseSelect'), getOptionValue: function (o) { return o.id; }, onChange: function (nextFactorId) {
                                            var _a, _b;
                                            var option = factorOptions.find(function (_a) {
                                                var id = _a.id;
                                                return id === nextFactorId;
                                            });
                                            setFactor(nextFactorId !== null && nextFactorId !== void 0 ? nextFactorId : '');
                                            setSubcategory((_b = (_a = option === null || option === void 0 ? void 0 : option.subcategories) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : NO_SUBCATEGORY);
                                        }, size: "small" }) })), _jsx(UnControlledFormField, __assign({ label: t('wizard:shrinkage.subcategory'), sx: { flex: 1 } }, { children: _jsx(Autocomplete, { options: hasSubcategories ? subcategoryOptions : [{ id: NO_SUBCATEGORY, name: t('translation:action.none') }], placeholder: t('translation:autocomplete.pleaseSelect'), disableClearable: false, value: hasSubcategories ? subcategory : NO_SUBCATEGORY, getOptionValue: function (o) { return o.id; }, onChange: function (nextSubcategory) { return setSubcategory(nextSubcategory !== null && nextSubcategory !== void 0 ? nextSubcategory : NO_SUBCATEGORY); }, size: "small", disabled: !hasSubcategories }) }))] })), _jsxs(Stack, __assign({ direction: "row", spacing: 4, alignItems: "flex-start" }, { children: [_jsx(UnControlledFormField, __assign({ label: t('quickInput.enterValue') }, { children: _jsx(NumberInput, { min: unitInputMeta.min, max: unitInputMeta.max, value: valueLocal, onChange: function (v) { return setValueLocal(v === null || v === '' ? null : Number(v)); }, suffix: unitInputMeta.suffix, size: "small", sx: { width: '6rem' }, decimal: unitInputMeta.decimal }) })), _jsxs(Box, { children: [hiddenLabel, _jsx(Autocomplete, { size: "small", options: unitOptions, getOptionValue: function (o) { return o.id; }, value: unitMode, onChange: handleUnitModeChange, disabled: !isUnitModeEnabled, sx: { width: '12rem' }, disableClearable: true })] }), !!queueLocations.length && (_jsx(UnControlledFormField, __assign({ label: t('quickInput.chooseLocation'), sx: { flex: 1 } }, { children: _jsx(MultiAutocomplete, { options: locationOptions, value: selectedLocations, onChange: function (val) { return setSelectedLocations(val); }, sx: { width: '100%' } }) }))), _jsxs(Box, { children: [hiddenLabel, _jsxs(RadioGroup, __assign({ row: true, value: shrinkageType, onChange: function (e) {
                                                var nextShrinkageType = e.target.value;
                                                setShrinkageType(nextShrinkageType);
                                                selectFirstFactor(category, nextShrinkageType);
                                            } }, { children: [_jsx(FormControlLabel, { value: "planned", control: _jsx(Radio, { size: "small" }), label: t('wizard:shrinkage.planned') }), _jsx(FormControlLabel, { value: "unplanned", control: _jsx(Radio, { size: "small" }), label: t('wizard:shrinkage.unplanned') })] }))] })] })), _jsx(PeriodSelector, { periodLabels: periodLabels, selectedWeeks: selectedWeeks, periodLength: periodLength, showDateColumn: showDateColumn, planType: planType, onSelectAll: handleSelectAll, onWeekToggle: handleWeekToggle })] })) }), _jsxs(StyledDialogActions, __assign({ sx: { justifyContent: 'space-between', width: '100%' } }, { children: [_jsx(StyledActionButton, __assign({ variant: "outlined", onClick: handleClose }, { children: t('translation:action.cancel') })), _jsxs(Stack, __assign({ direction: "row", spacing: 2 }, { children: [_jsx(StyledActionButton, __assign({ variant: "outlined", onClick: handleSaveAndAddAnother, disabled: !canSubmit }, { children: t('wizard:shrinkage.saveAndAddAnother') })), _jsx(StyledActionButton, __assign({ variant: "contained", onClick: handleDone, disabled: !canSubmit }, { children: t('action.done') }))] }))] }))] })));
};
